// #89 QueueGroup: the Coordinator Workbench's collapsible status-group heading (W-1, W-14). A 36 px button with the
// status dot (the status word's duty hue, the -text twin), the status word, the count and a chevron; aria-expanded and
// aria-controls name the group's rows, which stay in the DOM (hidden when collapsed). Each viewer's collapsed groups
// are remembered in localStorage under `memoryKey` (useViewerMemory, try/catch); under McRoot capture nothing is read
// or written and every group starts at its default (expanded unless the host says otherwise).

import type { ReactNode } from "react";
import { Count, Icon } from "../core";
import { isStatusWord, toneOf } from "../labels";
import { cx } from "../lib/cx";
import { useHs, type Hs } from "../lib/hs";
import { useDisclosure } from "../lib/useDisclosure";
import { useViewerMemory } from "../lib/viewerMemory";

export interface QueueGroupProps {
  /** The status word (one of the eight scheduling statuses). */
  status: string;
  count: number;
  /** The group's rows. */
  children: ReactNode;
  /** The per-viewer memory key (e.g. the status's key); absent, the group is not remembered. */
  memoryKey?: string | undefined;
  /** The state before the viewer has chosen (default open); capture always starts here. */
  defaultOpen?: boolean | undefined;
  /** Controlled open state (overrides the memory). */
  open?: boolean | undefined;
  onOpenChange?: ((open: boolean) => void) | undefined;
  /** The heading level of the group (default 3). */
  level?: 2 | 3 | 4 | undefined;
  /** The rows' container id (default generated). */
  id?: string | undefined;
  hs?: Hs | undefined;
  className?: string | undefined;
}

export function QueueGroup({ status, count, children, memoryKey, defaultOpen = true, open, onOpenChange, level = 3, id, hs, className }: QueueGroupProps) {
  const hot = useHs(hs);
  const [remembered, remember] = useViewerMemory(memoryKey ? `qgroup.${memoryKey}` : null, defaultOpen);
  const state = useDisclosure({
    open: open ?? remembered,
    onOpenChange: (next) => {
      if (open === undefined) remember(next);
      onOpenChange?.(next);
    },
    id,
  });
  const tone = isStatusWord(status) ? toneOf(status) : "neutral";
  const Heading = `h${level}` as const;
  return (
    <div className={cx("mc-qg", !state.open && "mc-qg-closed", className)}>
      <Heading className="mc-qg-hd">
        <button type="button" className="mc-qg-h" {...state.button} {...hot}>
          <i className={cx("mc-qg-dot", `mc-qg-${tone}`)} aria-hidden="true" />
          <span className="mc-qg-t">{status}</span>
          <Count n={count} />
          <Icon name={state.open ? "down" : "right"} size={16} className="mc-qg-chev" />
        </button>
      </Heading>
      <div className="mc-qg-b" id={state.panel.id} hidden={state.panel.hidden}>{children}</div>
    </div>
  );
}
